import type { User } from '@lmc/ts-common'
import { Avatar, Card, Heading } from '@lmc/ui-kit'
import { BackButton, Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { teamTranslations } from 'locale/team'
import { useTranslation } from 'react-i18next'
import { history } from 'store'
import styled from 'styled-components'

type TeamMemberSideNavigationProps = {
  carer: User
  testId?: string
}

const TeamMemberSideNavigationCard = ({
  carer,
  testId
}: TeamMemberSideNavigationProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  return (
    <Card data-testid={testId}>
      <BackButton
        onClick={() => history.push('/organisation/list')}
        data-testid={testId ? `${testId}-back-button` : undefined}
      >
        {t(teamTranslations.TEAM_MEMBERS_LIST)}
      </BackButton>
      <TeamMemberInfoWrapper
        data-testid={testId ? `${testId}-info-wrapper` : undefined}
      >
        <Avatar
          size={48}
          url={carer.picture as unknown as string}
          data-testid={testId ? `${testId}-avatar` : undefined}
        />
        <TextWrapper
          data-testid={testId ? `${testId}-text-wrapper` : undefined}
        >
          <CarerName
            level={4}
            data-testid={testId ? `${testId}-name` : undefined}
          >
            {carer.name?.first} {carer.name?.last}
          </CarerName>
          <CarerRole data-testid={testId ? `${testId}-role` : undefined}>
            {t(teamTranslations[carer.role])}
          </CarerRole>
          {carer.active ? (
            <CarerStatus
              $active
              level={2}
              data-testid={testId ? `${testId}-status-active` : undefined}
            >
              {t(teamTranslations.ACTIVE)}
            </CarerStatus>
          ) : (
            <CarerStatus
              level={2}
              data-testid={testId ? `${testId}-status-inactive` : undefined}
            >
              {t(teamTranslations.INACTIVE)}
            </CarerStatus>
          )}
        </TextWrapper>
      </TeamMemberInfoWrapper>
    </Card>
  )
}

const CarerName = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color1};
`

const CarerRole = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const CarerStatus = styled(Text)<{ $active?: boolean }>`
  color: ${(props) =>
    props?.$active
      ? props.theme.color.status.success
      : props.theme.color.status.danger};
`

const TeamMemberInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export default TeamMemberSideNavigationCard
